import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import DeleteIcon from '@mui/icons-material/Delete'

import axios from '../../dataClient/dataClient'
import { StyledDialog, FlexDialogContent, FlexEndContainer } from '../styles/Containers'
import { ErrorMessage, SuccessMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldSpan, BoldFormTitle } from '../styles/Text'

const typeLabel = {
  DRAFT: 'Draft',
  TEMPLATE: 'Template'
}

function DeleteCommunicationButton({ state, id, name, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const label = typeLabel[state]
  const labelLower = label[0].toLowerCase() + label.slice(1)
  const title = state === 'DRAFT' ? `Delete ${label}: ${name}` : `Delete Communication ${label}: ${name}`

  const handleOpen = () => setIsVisible(true)

  function handleClose() {
    setIsVisible(false)
    setError(false)
    setIsLoading(false)
    setIsSuccess(false)
    if (isSuccess) onSuccess(id)
  }

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  function handleConfirm() {
    setIsLoading(true)
    setError(false)

    const endpoint = state === 'DRAFT' ? `/communication/${id}` : `/communication/communication-template/${id}`

    axios
      .delete(endpoint)
      .then(() => {
        if (isMounted.current) setIsSuccess(true)
      })
      .catch(e => {
        if (isMounted.current) {
          setIsLoading(false)
          setError(true)
          console.error(`failed to delete communication ${labelLower}`, state, id, e)
        }
      })
  }

  return (
    <>
      <SecondaryButton onClick={handleOpen}>
        <DeleteIcon fontSize="small" />
        Delete
      </SecondaryButton>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false} maxwidth="700px">
        <FlexDialogContent>
          <BoldFormTitle>{title}</BoldFormTitle>
          {isSuccess ? (
            <>
              <SuccessMessage>{label} successfully deleted</SuccessMessage>
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose}>Close</SecondaryButton>
              </FlexEndContainer>
            </>
          ) : (
            <>
              <BodyText>
                Are you sure you want to delete the {labelLower} <BoldSpan>{name}</BoldSpan>?
              </BodyText>
              {error && <ErrorMessage>There was an error deleting the {labelLower}. Please try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button onClick={handleConfirm} loading={isLoading}>
                  Confirm
                </Button>
              </FlexEndContainer>
            </>
          )}
        </FlexDialogContent>
      </StyledDialog>
    </>
  )
}

DeleteCommunicationButton.propTypes = {
  state: PropTypes.oneOf(['DRAFT', 'TEMPLATE']).isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onSuccess: PropTypes.func
}

export default DeleteCommunicationButton
